import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { authService } from '../../services/authService';

const AdminSidebar = ({ isCollapsed = false, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await authService?.signOut();
      navigate('/admin-portal-login');
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setIsSigningOut(false);
    }
  };

  const menuSections = [
    {
      title: 'Overview',
      items: [
        { label: 'Admin Dashboard', path: '/admin-portal-dashboard', icon: 'LayoutDashboard' },
        { label: 'Control Panel', path: '/admin-control-panel', icon: 'Settings' },
        { label: 'Super Admin Portal', path: '/super-admin-portal', icon: 'ShieldAlert' },
        { label: 'Analytics', path: '/admin-control-panel#analytics', icon: 'BarChart3' },
      ],
    },
    {
      title: 'Sales',
      items: [
        { label: 'Pipeline Kanban', path: '/sales-pipeline-kanban-board', icon: 'Kanban' },
      ],
    },
    {
      title: 'User Management',
      items: [
        { label: 'Verification', path: '/admin-control-panel#verification', icon: 'ShieldCheck' },
        { label: 'Candidates', path: '/admin-control-panel#candidates', icon: 'Users' },
        { label: 'Employers', path: '/admin-control-panel#employers', icon: 'Building2' },
        { label: 'Access Requests', path: '/admin-control-panel#access-requests', icon: 'UserPlus' },
      ],
    },
    {
      title: 'Content',
      items: [
        { label: 'Reveal Approvals', path: '/admin-control-panel#reveal-approvals', icon: 'Shield' },
        { label: 'Reported Content', path: '/admin-control-panel#reported', icon: 'AlertTriangle' },
      ],
    },
    {
      title: 'System',
      items: [
        { label: 'Audit Logs', path: '/admin-control-panel#audit', icon: 'FileText' },
        { label: 'Settings', path: '/admin-control-panel#settings', icon: 'Settings' },
      ],
    },
  ];

  const isActive = (path) => {
    if (path?.includes('#')) {
      return location?.pathname === path?.split('#')?.[0] && location?.hash === `#${path?.split('#')?.[1]}`;
    }
    return location?.pathname === path;
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleLinkClick = () => {
    setIsMobileOpen(false);
  };

  return (
    <>
      <button
        className="admin-sidebar-toggle"
        onClick={handleMobileToggle}
        aria-label="Toggle admin menu"
      >
        <Icon name="Menu" size={24} />
      </button>
      {isMobileOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      <aside className={`admin-sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'translate-x-0' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-logo">
            <Icon name="Eye" size={24} color="#FFFFFF" />
          </div>
          <span className="admin-sidebar-logo-text">BlinD Admin</span>
        </div>

        <div className="admin-sidebar-menu">
          {menuSections?.map((section) => (
            <div key={section?.title} className="admin-sidebar-section">
              <h3 className="admin-sidebar-section-title">{section?.title}</h3>
              {section?.items?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={handleLinkClick}
                  className={`admin-sidebar-item ${isActive(item?.path) ? 'active' : ''}`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="admin-sidebar-item-label">{item?.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </div>

        {/* Sign Out Button */}
        <button
          onClick={handleSignOut}
          disabled={isSigningOut}
          className={`w-full flex items-center gap-3 px-4 py-3 text-error hover:bg-error/10 transition-colors rounded-xl ${
            isCollapsed ? 'justify-center' : ''
          } ${isSigningOut ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Icon name={isSigningOut ? 'Loader' : 'LogOut'} size={20} className={isSigningOut ? 'animate-spin' : ''} />
          {!isCollapsed && <span className="font-medium">{isSigningOut ? 'Signing out...' : 'Sign Out'}</span>}
        </button>
      </aside>
    </>
  );
};

export default AdminSidebar;